/**
 * VideoLibraryPage — /library
 *
 * Searchable, filterable grid of evaluations with Tier-1 + Tier-2 filters.
 *
 * Filter bar:
 *   - Template picker
 *   - Athlete picker (coach/admin only)
 *   - Channel picker
 *   - Date range (from / to)
 *   - Score range (min / max) — Tier-2
 *   - Body-part multi-select (observation_types) — Tier-2
 *   - Video status toggle (any / available / expired) — Tier-2
 *   - Sort field + order
 *
 * Results: card grid → each card links to /evaluations/{evaluation_id}.
 * Pagination: prev/next offset-based, showing "Showing X–Y of N".
 *
 * Role-awareness:
 *   - Athletes: athlete filter is hidden (backend also enforces this).
 *   - Coaches/admins: athlete filter visible.
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTemplates } from '@/hooks/useTemplates';
import { useMyChannels, useCoachAthletes } from '@/hooks/useChannels';
import { useEvaluationSearch } from '@/hooks/useEvaluationSearch';
import { Skeleton, Badge } from '@/components/ui';
import { formatDate } from '@/utils/formatters';
import type { EvaluationSearchParams } from '@/services/evaluationSearchApi';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

// Common body-part labels that appear in observation_types.
// Users can also type freeform labels.
const BODY_PART_OPTIONS = [
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle',
  'spine',
  'pelvis',
  'head',
] as const;

type SortBy = 'created_at' | 'overall_score' | 'form_score' | 'timing_score';
type Order = 'asc' | 'desc';
type VideoStatusFilter = 'any' | 'available' | 'expired';

const SORT_OPTIONS: { value: SortBy; label: string }[] = [
  { value: 'created_at', label: 'Date' },
  { value: 'overall_score', label: 'Overall Score' },
  { value: 'form_score', label: 'Form Score' },
  { value: 'timing_score', label: 'Timing Score' },
];

// ─── Shared input styles ──────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: '8px',
  border: '1px solid var(--color-border-subtle)',
  backgroundColor: 'var(--color-dark-elevated-1)',
  color: 'var(--color-text-primary)',
  fontSize: '13px',
  minWidth: '140px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: '4px',
  display: 'block',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function ScoreBadge({ score, label }: { score: number | null; label?: string }) {
  if (score === null) {
    return <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>—</span>;
  }
  const color =
    score >= 80
      ? 'var(--color-success)'
      : score >= 60
        ? 'var(--color-warning)'
        : 'var(--color-error)';
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1px',
      }}
    >
      {label && (
        <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', fontWeight: 500 }}>
          {label}
        </span>
      )}
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '999px',
          fontSize: '13px',
          fontWeight: 700,
          backgroundColor: `${color}22`,
          color,
        }}
      >
        {Math.round(score)}
      </span>
    </span>
  );
}

function VideoStatusBadge({ status }: { status: 'available' | 'expired' }) {
  if (status === 'expired') {
    return (
      <Badge variant="secondary" size="sm" title="Video file purged; score and feedback still accessible">
        Expired
      </Badge>
    );
  }
  return (
    <Badge variant="success" size="sm">
      Available
    </Badge>
  );
}

/** Loading skeleton card grid */
function SkeletonGrid() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px',
      }}
      aria-busy="true"
      aria-label="Loading evaluations"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Skeleton className="h-5 rounded w-3/4" />
          <Skeleton className="h-3 rounded w-1/2" />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Skeleton className="h-7 w-14 rounded-full" />
            <Skeleton className="h-7 w-14 rounded-full" />
            <Skeleton className="h-7 w-14 rounded-full" />
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <Skeleton className="h-4 w-16 rounded-full" />
            <Skeleton className="h-4 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function VideoLibraryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCoachOrAdmin = user?.role === 'coach' || user?.role === 'admin';

  // ── Filter state ─────────────────────────────────────────────────────────────
  const [templateId, setTemplateId] = useState('');
  const [athleteId, setAthleteId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [scoreMin, setScoreMin] = useState('');
  const [scoreMax, setScoreMax] = useState('');
  const [videoStatus, setVideoStatus] = useState<VideoStatusFilter>('any');
  const [selectedBodyParts, setSelectedBodyParts] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [order, setOrder] = useState<Order>('desc');
  const [offset, setOffset] = useState(0);

  // ── Dropdown data ─────────────────────────────────────────────────────────────
  const { data: templates } = useTemplates();
  const { data: channels } = useMyChannels();
  const channelSlugs = channels?.map((ch) => ch.slug) ?? [];
  const { data: athletes } = useCoachAthletes(
    isCoachOrAdmin ? channelSlugs : [],
    user?.id ? [user.id] : []
  );

  // ── Build search params ───────────────────────────────────────────────────────
  const searchParams = useMemo((): EvaluationSearchParams => {
    const p: EvaluationSearchParams = {
      limit: PAGE_SIZE,
      offset,
      sort_by: sortBy,
      order,
    };
    if (templateId) p.template_id = templateId;
    if (athleteId && isCoachOrAdmin) p.athlete_id = athleteId;
    if (channelId) p.channel_id = channelId;
    if (dateFrom) p.date_from = new Date(dateFrom).toISOString();
    if (dateTo) p.date_to = new Date(dateTo).toISOString();
    const min = parseFloat(scoreMin);
    if (!isNaN(min)) p.score_min = min;
    const max = parseFloat(scoreMax);
    if (!isNaN(max)) p.score_max = max;
    if (videoStatus !== 'any') p.video_status = videoStatus;
    if (selectedBodyParts.length > 0) p.body_part = selectedBodyParts;
    return p;
  }, [
    offset, sortBy, order, templateId, athleteId, channelId,
    dateFrom, dateTo, scoreMin, scoreMax, videoStatus, selectedBodyParts,
    isCoachOrAdmin,
  ]);

  const { items, total, hasMore, loading, error } = useEvaluationSearch(searchParams);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const resetPage = useCallback(() => setOffset(0), []);

  function handleFilterChange(setter: (v: string) => void) {
    return (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      setter(e.target.value);
      resetPage();
    };
  }

  function toggleBodyPart(part: string) {
    setSelectedBodyParts((prev) =>
      prev.includes(part) ? prev.filter((p) => p !== part) : [...prev, part]
    );
    resetPage();
  }

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromRow = total === 0 ? 0 : offset + 1;
  const toRow = Math.min(offset + PAGE_SIZE, total);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ marginBottom: '20px' }}>
        <h1
          style={{
            fontSize: '22px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: '4px',
          }}
        >
          Video Library
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0 }}>
          Browse and filter all your evaluation recordings.
        </p>
      </div>

      {/* Filter bar */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
        }}
      >
        {/* Row 1 — core filters */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'flex-end',
            marginBottom: '16px',
          }}
        >
          {/* Template */}
          <div>
            <label style={labelStyle} htmlFor="lib-template">Template</label>
            <select
              id="lib-template"
              value={templateId}
              onChange={handleFilterChange(setTemplateId)}
              style={inputStyle}
              aria-label="Filter by template"
            >
              <option value="">All templates</option>
              {templates?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {/* Athlete — coach/admin only */}
          {isCoachOrAdmin && (
            <div>
              <label style={labelStyle} htmlFor="lib-athlete">Athlete</label>
              <select
                id="lib-athlete"
                value={athleteId}
                onChange={handleFilterChange(setAthleteId)}
                style={inputStyle}
                aria-label="Filter by athlete"
              >
                <option value="">All athletes</option>
                {athletes?.map((a) => (
                  <option key={a.user_id} value={a.user_id}>
                    {a.display_name || a.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Channel */}
          <div>
            <label style={labelStyle} htmlFor="lib-channel">Channel</label>
            <select
              id="lib-channel"
              value={channelId}
              onChange={handleFilterChange(setChannelId)}
              style={inputStyle}
              aria-label="Filter by channel"
            >
              <option value="">All channels</option>
              {channels?.map((ch) => (
                <option key={String(ch.id)} value={String(ch.id)}>
                  {ch.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Date from */}
          <div>
            <label style={labelStyle} htmlFor="lib-date-from">From</label>
            <input
              id="lib-date-from"
              type="date"
              value={dateFrom}
              onChange={handleFilterChange(setDateFrom)}
              style={inputStyle}
              aria-label="Filter from date"
            />
          </div>

          {/* Date to */}
          <div>
            <label style={labelStyle} htmlFor="lib-date-to">To</label>
            <input
              id="lib-date-to"
              type="date"
              value={dateTo}
              onChange={handleFilterChange(setDateTo)}
              style={inputStyle}
              aria-label="Filter to date"
            />
          </div>

          {/* Sort */}
          <div>
            <label style={labelStyle} htmlFor="lib-sort">Sort by</label>
            <select
              id="lib-sort"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortBy);
                resetPage();
              }}
              style={inputStyle}
              aria-label="Sort by"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Order */}
          <div>
            <label style={labelStyle} htmlFor="lib-order">Order</label>
            <select
              id="lib-order"
              value={order}
              onChange={(e) => {
                setOrder(e.target.value as Order);
                resetPage();
              }}
              style={inputStyle}
              aria-label="Sort order"
            >
              <option value="desc">Newest / Highest first</option>
              <option value="asc">Oldest / Lowest first</option>
            </select>
          </div>
        </div>

        {/* Row 2 — Tier-2 filters */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            alignItems: 'flex-end',
          }}
        >
          {/* Score min */}
          <div>
            <label style={labelStyle} htmlFor="lib-score-min">Min score</label>
            <input
              id="lib-score-min"
              type="number"
              min="0"
              max="100"
              placeholder="0"
              value={scoreMin}
              onChange={handleFilterChange(setScoreMin)}
              style={{ ...inputStyle, minWidth: '80px', maxWidth: '90px' }}
              aria-label="Minimum overall score"
            />
          </div>

          {/* Score max */}
          <div>
            <label style={labelStyle} htmlFor="lib-score-max">Max score</label>
            <input
              id="lib-score-max"
              type="number"
              min="0"
              max="100"
              placeholder="100"
              value={scoreMax}
              onChange={handleFilterChange(setScoreMax)}
              style={{ ...inputStyle, minWidth: '80px', maxWidth: '90px' }}
              aria-label="Maximum overall score"
            />
          </div>

          {/* Video status */}
          <div>
            <label style={labelStyle} htmlFor="lib-video-status">Video</label>
            <select
              id="lib-video-status"
              value={videoStatus}
              onChange={(e) => {
                setVideoStatus(e.target.value as VideoStatusFilter);
                resetPage();
              }}
              style={inputStyle}
              aria-label="Filter by video status"
            >
              <option value="any">Any status</option>
              <option value="available">Video available</option>
              <option value="expired">Video expired</option>
            </select>
          </div>

          {/* Body-part multi-select chips */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <span style={labelStyle}>Body parts observed</span>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                paddingTop: '2px',
              }}
            >
              {BODY_PART_OPTIONS.map((part) => {
                const selected = selectedBodyParts.includes(part);
                return (
                  <button
                    key={part}
                    type="button"
                    onClick={() => toggleBodyPart(part)}
                    aria-pressed={selected}
                    style={{
                      padding: '3px 9px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      border: selected
                        ? '1px solid var(--color-accent)'
                        : '1px solid var(--color-border-subtle)',
                      backgroundColor: selected ? 'var(--color-accent)22' : 'transparent',
                      color: selected ? 'var(--color-accent)' : 'var(--color-text-muted)',
                      transition: 'all 100ms ease',
                    }}
                  >
                    {part.replace(/_/g, ' ')}
                  </button>
                );
              })}
              {selectedBodyParts.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedBodyParts([]);
                    resetPage();
                  }}
                  style={{
                    padding: '3px 9px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    border: '1px solid var(--color-error)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-error)',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div
        style={{
          fontSize: '13px',
          color: 'var(--color-text-muted)',
          marginBottom: '16px',
          minHeight: '20px',
        }}
      >
        {loading
          ? 'Loading...'
          : error
            ? 'Error loading results.'
            : total === 0
              ? 'No evaluations found.'
              : `Showing ${fromRow}–${toRow} of ${total}`}
      </div>

      {/* Loading skeleton */}
      {loading && <SkeletonGrid />}

      {/* Error state */}
      {!loading && error && (
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-error)',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center',
            color: 'var(--color-error)',
          }}
          role="alert"
        >
          <p style={{ fontWeight: 600, marginBottom: '4px' }}>Failed to load evaluations.</p>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
            Check your connection and try refreshing the page.
          </p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && items.length === 0 && (
        <div
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '12px',
            padding: '48px 24px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              marginBottom: '8px',
            }}
          >
            No evaluations found
          </p>
          <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
            Try adjusting the filters above, or upload your first evaluation.
          </p>
        </div>
      )}

      {/* Results grid */}
      {!loading && !error && items.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '16px',
          }}
        >
          {items.map((item) => (
            <article
              key={item.evaluation_id}
              onClick={() => navigate(`/evaluations/${item.evaluation_id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/evaluations/${item.evaluation_id}`);
                }
              }}
              role="link"
              tabIndex={0}
              aria-label={`Evaluation: ${item.template_name ?? 'Unknown template'} — ${item.athlete_display_name ?? 'Unknown athlete'}`}
              style={{
                backgroundColor: 'var(--color-dark-card)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '12px',
                padding: '16px',
                cursor: 'pointer',
                transition: 'border-color 120ms ease, background-color 120ms ease',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-accent)';
                (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-dark-elevated-1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border-subtle)';
                (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-dark-card)';
              }}
              onFocus={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-accent)';
              }}
              onBlur={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border-subtle)';
              }}
            >
              {/* Template name + status badge */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <h2
                  style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    margin: 0,
                    lineHeight: 1.3,
                  }}
                >
                  {item.template_name ?? 'Unknown template'}
                </h2>
                <VideoStatusBadge status={item.video_status} />
              </div>

              {/* Athlete name + date */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {item.athlete_display_name && (
                  <span
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.athlete_display_name}
                  </span>
                )}
                <span
                  style={{
                    fontSize: '12px',
                    color: 'var(--color-text-muted)',
                    whiteSpace: 'nowrap',
                    marginLeft: 'auto',
                  }}
                >
                  {formatDate(item.created_at)}
                </span>
              </div>

              {/* Score row */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <ScoreBadge score={item.overall_score} label="Overall" />
                <ScoreBadge score={item.form_score} label="Form" />
                <ScoreBadge score={item.timing_score} label="Timing" />
              </div>

              {/* Observation type chips */}
              {item.observation_types && item.observation_types.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {item.observation_types.slice(0, 5).map((obs) => (
                    <span
                      key={obs}
                      style={{
                        fontSize: '10px',
                        fontWeight: 500,
                        padding: '2px 7px',
                        borderRadius: '999px',
                        backgroundColor: 'var(--color-dark-elevated-2)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      {obs.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {item.observation_types.length > 5 && (
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 500,
                        padding: '2px 7px',
                        borderRadius: '999px',
                        backgroundColor: 'var(--color-dark-elevated-2)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      +{item.observation_types.length - 5}
                    </span>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '24px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-1)',
              color: offset === 0 ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
              cursor: offset === 0 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
            }}
          >
            Prev
          </button>
          <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!hasMore}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-1)',
              color: hasMore ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              cursor: hasMore ? 'pointer' : 'not-allowed',
              fontSize: '13px',
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
